import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Send,
  Close
} from '@mui/icons-material';

import { gatewayWhatsappService } from '../../services/gatewayWhatsappService';
import { SysEditorWhatsapp } from '../syseditor';
import { GatewayWhatsappSelect } from '../selects/GatewayWhatsappSelect';

interface EnviarWhatsappMsgFormProps {
  open: boolean;
  onClose: () => void;
  destinatario: string;
  destinatarioNome?: string;
  loading?: boolean;
  contatoId?: number;
}

const EnviarWhatsappMsgForm: React.FC<EnviarWhatsappMsgFormProps> = ({
  open,
  onClose,
  destinatario,
  destinatarioNome,
  loading: externalLoading = false,
  contatoId,
}) => {
  const [selectedGateway, setSelectedGateway] = useState<number | ''>(9999);
  const [mensagem, setMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);


  useEffect(() => {
    if (open) {
      // Reset form
      setMensagem('');
      setSelectedGateway(9999);
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!mensagem.trim()) {
      setError('Digite uma mensagem');
      return;
    }

    if (!selectedGateway) {
      setError('Selecione um gateway WhatsApp');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const result = await gatewayWhatsappService.sendWhatsApp({
        contato_id: contatoId,
        message: mensagem.trim(),
        gateway_id: selectedGateway === 9999 ? undefined : Number(selectedGateway)
      });

      if (result.success) {
        setSuccess('WhatsApp enviado com sucesso!');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Erro ao enviar WhatsApp');
      }
    } catch (err: any) {
      console.error('Erro ao enviar WhatsApp:', err);
      setError(err.message || 'Erro ao enviar WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !externalLoading) {
      onClose();
    }
  };

  const isFormDisabled = loading || externalLoading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isFormDisabled}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Send color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Enviar WhatsApp
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {/* Informações do destinatário */}
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <b>Destinatário:</b> {destinatarioNome && (
                <>
                  {destinatarioNome} -{' '}
                </>
              )}
              {destinatario}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Seleção do Gateway WhatsApp */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <GatewayWhatsappSelect
              value={selectedGateway}
              onChange={setSelectedGateway}
              label="Gateway WhatsApp"
              required
              disabled={isFormDisabled}
              helperText="Selecione o gateway para envio da mensagem"
            />
          </Box>

          {/* Editor de mensagem */}
          <Box sx={{ mt: 2 }}>
            <SysEditorWhatsapp
              value={mensagem}
              onChange={setMensagem}
              placeholder="Digite sua mensagem aqui..."
              maxLength={1000}
              disabled={isFormDisabled}
              showPreview={true}
              rows={4}
              label="Mensagem *"
              helperText="Use formatação *negrito* e _itálico_ para destacar texto"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={isFormDisabled}
            startIcon={<Close />}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isFormDisabled || !mensagem.trim() || !selectedGateway}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          >
            {loading ? 'Enviando...' : 'Enviar WhatsApp'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EnviarWhatsappMsgForm; 